import React, { useMemo, useState } from 'react';
// Styling (.dt-*) lives in src/index.css alongside the other shared table classes
// (.table-modern, .table-wrapper-modern, ...) this component builds on.

// A column can be a plain label ('IP Addr') or { label, field, sortable, render }.
// The field key (used for searching/sorting) is derived from the label when not given.
function slug(label) {
    return String(label).toLowerCase().replace(/[^a-z0-9]/g, '');
}
function normalizeColumns(columns) {
    return columns.map((c) => (typeof c === 'string' ? { label: c, field: slug(c) } : { field: slug(c.label), ...c }));
}
function cellValue(row, column) {
    return column.render ? column.render(row) : row[column.field];
}
// Sorting and "does this row match the search" both need a plain string/number,
// not whatever JSX render() produced.
function searchValue(row, column) {
    return column.searchValue ? column.searchValue(row) : row[column.field];
}

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

/**
 * The classic DataTables look (Show N entries / Search / sortable headers /
 * "Showing X to Y of Z entries" / Previous · page numbers · Next), built as a
 * plain React component — no jQuery, so it composes with the rest of the app.
 *
 * Row selection is controlled: pass `selectable`, `selectedIds` (an array of
 * `row.id`) and `onSelectionChange` to get a checkbox column with a "select
 * all on this page" header checkbox — the caller owns the selection state
 * (so e.g. a "Delete Selected" button can clear it after the delete).
 *
 * A column can also carry `filter: true` (a text box) or `filter: { options: [...] }`
 * (a select) to get a second header row of per-column filters (all ANDed together,
 * and with the global search box) — set on at least one column to turn it on.
 *
 * @param {(string|{label:string, field?:string, sortable?:boolean, render?:(row)=>node, searchValue?:(row)=>string, filter?:true|{options:string[], placeholder?:string}})[]} columns
 * @param {object[]} rows - plain objects keyed by each column's field
 * @param {(row)=>string} [rowClassName] - extra class name(s) for a row, e.g. to tint failed rows red
 */
export default function DataTable({ columns, rows, defaultPageSize = 10, emptyMessage = 'No data available in table', selectable = false, selectedIds = [], onSelectionChange, rowClassName }) {
    const cols = useMemo(() => normalizeColumns(columns), [columns]);
    const [pageSize, setPageSize] = useState(defaultPageSize);
    const [search, setSearch] = useState('');
    const [colFilters, setColFilters] = useState({});
    const [sort, setSort] = useState({ field: null, dir: 1 });
    const [page, setPage] = useState(1);
    const hasColumnFilters = cols.some((c) => c.filter);

    function setColFilter(field, value) {
        setColFilters((prev) => ({ ...prev, [field]: value }));
        setPage(1);
    }

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return rows.filter((row) => {
            if (q && !cols.some((c) => String(searchValue(row, c) ?? '').toLowerCase().includes(q))) return false;
            for (const c of cols) {
                const fv = colFilters[c.field];
                if (!fv) continue;
                const val = String(searchValue(row, c) ?? '');
                if (c.filter?.options ? val !== fv : !val.toLowerCase().includes(fv.toLowerCase())) return false;
            }
            return true;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rows, search, colFilters, columns]);

    const sorted = useMemo(() => {
        const column = cols.find((c) => c.field === sort.field);
        if (!column) return filtered;
        const copy = [...filtered];
        copy.sort((a, b) => {
            const av = searchValue(a, column) ?? '';
            const bv = searchValue(b, column) ?? '';
            const an = parseFloat(String(av).replace(/[^0-9.-]/g, ''));
            const bn = parseFloat(String(bv).replace(/[^0-9.-]/g, ''));
            const bothNumeric = String(av).trim() !== '' && String(bv).trim() !== '' && !isNaN(an) && !isNaN(bn);
            const cmp = bothNumeric ? an - bn : String(av).localeCompare(String(bv));
            return cmp * sort.dir;
        });
        return copy;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filtered, sort, columns]);

    const total = sorted.length;
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    const currentPage = Math.min(page, totalPages);
    const start = total === 0 ? 0 : (currentPage - 1) * pageSize + 1;
    const end = Math.min(currentPage * pageSize, total);
    const pageRows = sorted.slice(start - 1, end);

    function toggleSort(field) {
        setSort((prev) => (prev.field === field ? { field, dir: -prev.dir } : { field, dir: 1 }));
        setPage(1);
    }

    const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
    const allOnPageSelected = selectable && pageRows.length > 0 && pageRows.every((r) => selectedSet.has(r.id));

    function toggleRow(id) {
        const next = new Set(selectedSet);
        if (next.has(id)) next.delete(id); else next.add(id);
        onSelectionChange?.(Array.from(next));
    }
    function toggleAllOnPage() {
        const next = new Set(selectedSet);
        pageRows.forEach((r) => (allOnPageSelected ? next.delete(r.id) : next.add(r.id)));
        onSelectionChange?.(Array.from(next));
    }

    const headerRow = (
        <tr>
            {selectable && (
                <th className="dt-select-col">
                    <input type="checkbox" checked={allOnPageSelected} onChange={toggleAllOnPage} aria-label="Select all rows on this page" />
                </th>
            )}
            {cols.map((c) => (
                <th
                    key={c.field}
                    className={c.sortable === false ? '' : 'dt-sortable'}
                    onClick={() => c.sortable !== false && toggleSort(c.field)}
                >
                    {c.label}
                    {c.sortable !== false && (
                        <i className={`fas dt-sort-icon ${sort.field === c.field ? (sort.dir === 1 ? 'fa-sort-up' : 'fa-sort-down') : 'fa-sort'}`}></i>
                    )}
                </th>
            ))}
        </tr>
    );

    return (
        <div className="dt-modern">
            <div className="dt-toolbar">
                <label className="dt-page-size">
                    Show
                    <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}>
                        {PAGE_SIZE_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
                    </select>
                    entries
                </label>
                <label className="dt-search">
                    Search:
                    <input type="text" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
                </label>
            </div>

            <div className="table-container">
                <table className="table-modern dt-table">
                    <thead>
                        {headerRow}
                        {hasColumnFilters && (
                            <tr className="dt-filter-row">
                                {selectable && <th></th>}
                                {cols.map((c) => (
                                    <th key={c.field}>
                                        {c.filter?.options ? (
                                            <select value={colFilters[c.field] || ''} onChange={(e) => setColFilter(c.field, e.target.value)} aria-label={`Filter ${c.label}`}>
                                                <option value="">All</option>
                                                {c.filter.options.map((o) => <option key={o} value={o}>{o}</option>)}
                                            </select>
                                        ) : c.filter ? (
                                            <input
                                                type="text"
                                                placeholder={c.filter.placeholder || c.label}
                                                value={colFilters[c.field] || ''}
                                                onChange={(e) => setColFilter(c.field, e.target.value)}
                                                aria-label={`Filter ${c.label}`}
                                            />
                                        ) : null}
                                    </th>
                                ))}
                            </tr>
                        )}
                    </thead>
                    <tbody>
                        {pageRows.length === 0 ? (
                            <tr><td colSpan={cols.length + (selectable ? 1 : 0)} className="table-empty-row">{emptyMessage}</td></tr>
                        ) : (
                            pageRows.map((row, i) => (
                                <tr key={row.id ?? i} className={rowClassName?.(row) || undefined}>
                                    {selectable && (
                                        <td className="dt-select-col">
                                            <input type="checkbox" checked={selectedSet.has(row.id)} onChange={() => toggleRow(row.id)} aria-label={`Select row ${row.id}`} />
                                        </td>
                                    )}
                                    {cols.map((c) => <td key={c.field}>{cellValue(row, c)}</td>)}
                                </tr>
                            ))
                        )}
                    </tbody>
                    <tfoot>{headerRow}</tfoot>
                </table>
            </div>

            <div className="dt-footer">
                <span className="dt-info">Showing {start} to {end} of {total} entries</span>
                <div className="table-pagination">
                    <button className="pagination-btn" disabled={currentPage === 1} onClick={() => setPage((p) => p - 1)}>Previous</button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
                        <button key={n} className={`page-number ${n === currentPage ? 'active' : ''}`} onClick={() => setPage(n)}>{n}</button>
                    ))}
                    <button className="pagination-btn" disabled={currentPage === totalPages} onClick={() => setPage((p) => p + 1)}>Next</button>
                </div>
            </div>
        </div>
    );
}
